import { logger } from "@/utils/logger";

/**
 * Utility functions for Progressive Web App functionality
 */

/**
 * Detects if the app is running in PWA standalone mode
 * @returns boolean indicating if app is in standalone mode
 */
export const isPwaMode = (): boolean => {
  // Prioritize standard ways to detect PWA installation
  const standardPwaCheck =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true;

  if (standardPwaCheck) {
    logger.info("PWA detected via standard display-mode check");
    return true;
  }

  // Keep the iOS standalone check explicitly as it's a reliable indicator
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
  if (isIOS && (window.navigator as any).standalone) {
    logger.info("PWA detected via iOS standalone mode");
    return true;
  }

  // Removed other checks (referrer, URL params, cookies, localStorage, auth tokens in hash)
  // as they can cause false positives in a regular browser context.
  // These checks might be relevant for initial auth flows or specific app states,
  // but not for the fundamental definition of running in a standalone PWA window.

  // logger.info("Not in PWA mode");
  return false;
};

/**
 * Detects if the app is running on a mobile device
 * @returns boolean indicating if app is on mobile
 */
export const isMobileDevice = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * Detects if the app is running on iOS 
 * @returns boolean indicating if app is on iOS
 */
export const isIOSDevice = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
};

/**
 * Checks if the app should use server-side auth flow
 * @returns boolean indicating if server-side auth should be used
 */
export const useServerSideAuth = (): boolean => {
  // For PWA mode detection, use a consistent approach  
  const isPwa = isPwaMode();
  const isIOS = isIOSDevice();

  // logger.info("useServerSideAuth check:", { isPwa, isIOS });

  // Reset PWA detection if explicitly requested via URL
  if (window.location.href.includes('?resetPwa=true') ||
    window.location.href.includes('&resetPwa=true')) {
    logger.info("Resetting PWA state due to URL parameter");
    try {
      localStorage.removeItem('app_pwa_mode');
      localStorage.removeItem('use_server_auth');
      localStorage.removeItem('app_ios_pwa');

      // Clear cookies
      document.cookie = "app_pwa_mode=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      document.cookie = "mobile_app_login=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      document.cookie = "auth_in_progress=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    } catch (e) {
      logger.error('Failed to reset PWA state:', e);
    }
  }

  // Check if server auth is forced via URL parameter
  const forceServerAuth =
    window.location.href.includes('?serverAuth=true') ||
    window.location.href.includes('&serverAuth=true');

  if (forceServerAuth) {
    logger.info("Server-side auth forced via URL parameter");
    try {
      localStorage.setItem('use_server_auth', 'true');
    } catch (e) {
      logger.error('Failed to save auth preference:', e);
    }
    return true;
  }

  // IMPORTANT CHANGE: Only use server-side auth for PWA mode
  // All regular mobile browsers should use client-side auth like desktop
  if (isPwa) {
    logger.info("Using server-side auth for PWA mode");
    try {
      localStorage.setItem('use_server_auth', 'true');
      localStorage.setItem('app_pwa_mode', 'true');
    } catch (e) {
      logger.error('Failed to save auth preference for PWA:', e);
    }
    return true;
  }

  // All other cases (including mobile browsers) use client-side auth
  // logger.info("Using client-side auth for browser");
  try {
    localStorage.removeItem('use_server_auth');
  } catch (e) {
    logger.error('Failed to remove auth preference:', e);
  }
  return false;
};

// Maximum age for OAuth state (10 minutes in milliseconds)
const OAUTH_STATE_MAX_AGE = 10 * 60 * 1000;

// Key used for storing state in sessionStorage
const OAUTH_STATE_STORAGE_KEY = 'oauth_state';

/**
 * Interface for OAuth state object with timestamp for expiration checks
 */
interface OAuthStateData {
  value: string;
  timestamp: number;
  nonce?: string;
}

/**
 * Creates a state parameter with enhanced security for CSRF protection in OAuth flows
 * @returns string containing a random state parameter
 * @throws Error if crypto API is not available
 */
export const generateOAuthState = (): string => {
  try {
    if (!window.crypto || !window.crypto.getRandomValues) {
      throw new Error('Crypto API not available');
    }

    // Generate 24 bytes (192 bits) of random data for stronger security
    const array = new Uint8Array(24);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    logger.error('Failed to generate secure OAuth state:', error);
    // Fallback to less secure but functional alternative
    return Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15) +
      Date.now().toString(36);
  }
};

/**
 * Stores CSRF state in sessionStorage with timestamp
 * @param state The CSRF state to store
 * @returns boolean indicating if state was successfully stored
 */
export const storeOAuthState = (state: string): boolean => {
  try {
    if (!state || typeof state !== 'string' || state.length < 10) {
      logger.error('Invalid OAuth state value');
      return false;
    }

    // Clean up any expired states first
    cleanupExpiredOAuthStates();

    // Create state object with timestamp
    const stateData: OAuthStateData = {
      value: state,
      timestamp: Date.now(),
      nonce: generateOAuthState().substring(0, 8) // Add a nonce for uniqueness
    };

    // Store the state data
    sessionStorage.setItem(OAUTH_STATE_STORAGE_KEY, JSON.stringify(stateData));
    return true;
  } catch (error) {
    logger.error('Failed to store OAuth state:', error);
    return false;
  }
};

/**
 * Validates returned OAuth state against stored state with expiration check
 * @param returnedState The state parameter returned from OAuth provider
 * @returns boolean indicating if state is valid and not expired
 */
export const validateOAuthState = (returnedState: string): boolean => {
  try {
    if (!returnedState || typeof returnedState !== 'string') {
      logger.error('Invalid returned OAuth state');
      return false;
    }

    // Get stored state
    const storedStateJson = sessionStorage.getItem(OAUTH_STATE_STORAGE_KEY);
    if (!storedStateJson) {
      logger.error('No stored OAuth state found');
      return false;
    }

    // Parse stored state data
    let stateData: OAuthStateData;
    try {
      stateData = JSON.parse(storedStateJson);
    } catch (e) {
      logger.error('Invalid stored OAuth state format');
      return false;
    }

    // Check if state has expired
    const currentTime = Date.now();
    if (currentTime - stateData.timestamp > OAUTH_STATE_MAX_AGE) {
      logger.error('OAuth state has expired');
      clearOAuthState(); // Clean up expired state
      return false;
    }

    // Validate the state value
    const isValid = stateData.value === returnedState;

    // Clear the state after validation regardless of result
    // This prevents replay attacks
    clearOAuthState();

    return isValid;
  } catch (error) {
    logger.error('Error validating OAuth state:', error);
    return false;
  }
};

/**
 * Clears stored OAuth state
 * @returns boolean indicating if state was successfully cleared
 */
export const clearOAuthState = (): boolean => {
  try {
    sessionStorage.removeItem(OAUTH_STATE_STORAGE_KEY);
    return true;
  } catch (error) {
    logger.error('Failed to clear OAuth state:', error);
    return false;
  }
};

/**
 * Cleans up expired OAuth states from storage
 */
const cleanupExpiredOAuthStates = (): void => {
  try {
    const storedStateJson = sessionStorage.getItem(OAUTH_STATE_STORAGE_KEY);
    if (!storedStateJson) return;

    try {
      const stateData = JSON.parse(storedStateJson) as OAuthStateData;
      const currentTime = Date.now();

      // If the state has expired, remove it
      if (currentTime - stateData.timestamp > OAUTH_STATE_MAX_AGE) {
        sessionStorage.removeItem(OAUTH_STATE_STORAGE_KEY);
      }
    } catch (e) {
      // If the stored data is invalid, remove it
      sessionStorage.removeItem(OAUTH_STATE_STORAGE_KEY);
    }
  } catch (error) {
    logger.error('Error cleaning up expired OAuth states:', error);
  }
};

/**
 * Checks if local storage is available for OAuth state storage
 * @returns boolean indicating if storage is available
 */
export const isStorageAvailable = (): boolean => {
  try {
    const testKey = '_test_storage_';
    sessionStorage.setItem(testKey, testKey);
    const result = sessionStorage.getItem(testKey) === testKey;
    sessionStorage.removeItem(testKey);
    return result;
  } catch (e) {
    return false;
  }
};

/**
 * Mark the app as being in PWA mode in localStorage
 * This helps with detection on subsequent visits
 */
export const markAsPwa = (): void => {
  try {
    localStorage.setItem('app_pwa_mode', 'true');
    localStorage.setItem('use_server_auth', 'true');
    logger.info('App marked as PWA mode with server-side auth');

    // If we're in iOS, add an additional marker
    if (isIOSDevice()) {
      localStorage.setItem('app_ios_pwa', 'true');
      logger.info('App additionally marked as iOS PWA');
    }

    // Also set a cookie for environments where localStorage might be unavailable
    try {
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 30); // 30 days
      document.cookie = `app_pwa_mode=true; path=/; expires=${expiryDate.toUTCString()}`;
    } catch (e) {
      logger.error('Failed to set PWA cookie:', e);
    }
  } catch (e) {
    logger.error('Failed to mark app as PWA:', e);
  }
};

/**
 * Check if the app was loaded from a PWA state parameter
 * and persist that state if needed
 */
export const checkAndPersistPwaState = (): void => {
  try {
    // Check URL search params
    const url = new URL(window.location.href);
    if (url.searchParams.has('pwa')) {
      logger.info('PWA parameter detected in URL');
      markAsPwa();

      // Clean up URL if needed
      if (window.history && window.history.replaceState) {
        url.searchParams.delete('pwa');
        window.history.replaceState({}, document.title, url.toString());
      }
    }

    // Also check URL hash fragments
    if (window.location.hash && window.location.hash.includes('pwa=true')) {
      logger.info('PWA parameter detected in URL hash');
      markAsPwa();
    }

    // Check if auth tokens are present in the URL hash
    if (window.location.hash && (
      window.location.hash.includes('access_token=') ||
      window.location.hash.includes('provider_token=')
    )) {
      logger.info('Auth tokens detected in URL hash, likely PWA authentication');
      markAsPwa();

      // Extract and store tokens for backup
      try {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const providerToken = hashParams.get('provider_token');
        const providerRefreshToken = hashParams.get('provider_refresh_token');

        if (accessToken) {
          localStorage.setItem('sb-access-token-backup', accessToken);
          logger.info('Stored access token in localStorage backup');
        }

        if (refreshToken) {
          localStorage.setItem('sb-refresh-token-backup', refreshToken);
          logger.info('Stored refresh token in localStorage backup');
        }

        if (providerToken) {
          localStorage.setItem('provider-token-backup', providerToken);
          logger.info('Stored provider token in localStorage backup');
        }

        if (providerRefreshToken) {
          localStorage.setItem('provider-refresh-token-backup', providerRefreshToken);
          logger.info('Stored provider refresh token in localStorage backup');
        }

        localStorage.setItem('token-backup-timestamp', Date.now().toString());
      } catch (e) {
        logger.error('Failed to store tokens from URL hash:', e);
      }
    }
  } catch (e) {
    logger.error('Error checking PWA state from URL:', e);
  }
};

/**
 * Reset service worker cache for auth-related content
 * This helps fix issues with cached auth states - optimized
 */
export const resetServiceWorkerCache = async (): Promise<boolean> => {
  try {
    // Early exit if service workers aren't supported
    if (!('serviceWorker' in navigator)) {
      logger.info('Service worker not supported');
      return false;
    }

    // If no controller yet, try to register one for iOS PWA mode
    if (!navigator.serviceWorker.controller) {
      logger.info('No service worker controlling this page yet');

      // If on iOS PWA mode, try to register one
      if (isIOSDevice() && isPwaMode()) {
        try {
          // Register and activate service worker immediately
          const registration = await navigator.serviceWorker.register('/service-worker.js', {
            updateViaCache: 'none', // Don't use cached service worker
            scope: '/'
          });

          logger.info('Service worker registered for iOS PWA');

          // If the service worker isn't active yet, wait for it
          if (registration.installing || registration.waiting) {
            // Wait for the service worker to be ready
            await new Promise<void>((resolve) => {
              // Set up event listener for state changes
              const serviceWorker = registration.installing || registration.waiting;
              if (!serviceWorker) {
                resolve();
                return;
              }

              serviceWorker.addEventListener('statechange', () => {
                if (serviceWorker.state === 'activated') {
                  resolve();
                }
              });

              // Resolve anyway after 1 second to avoid blocking too long
              setTimeout(resolve, 1000);
            });
          }
        } catch (err) {
          logger.error('Failed to register service worker for iOS PWA:', err);
        }
      }

      // Check again if we now have a controller
      if (!navigator.serviceWorker.controller) {
        logger.info('Still no service worker controller after registration attempt');
        return false;
      }
    }

    // Clear auth cache with optimized message
    const clearMessage = {
      type: 'CLEAR_AUTH_CACHE',
      timestamp: Date.now() // Add timestamp to prevent message deduplication
    };
    navigator.serviceWorker.controller.postMessage(clearMessage);
    logger.info('Auth cache clear message sent to service worker');

    // Tell service worker to bypass auth requests
    const bypassMessage = {
      type: 'BYPASS_AUTH_REQUESTS',
      timestamp: Date.now() // Add timestamp to prevent message deduplication
    };
    navigator.serviceWorker.controller.postMessage(bypassMessage);
    logger.info('Auth bypass message sent to service worker');

    return true;
  } catch (e) {
    logger.error('Error resetting service worker cache:', e);
    return false;
  }
};

/**
 * Clear only auth-related cache entries from service worker - optimized
 */
export const clearAuthCache = async (): Promise<boolean> => {
  try {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
      logger.info('Service worker not available');
      return false;
    }

    // Send clear auth cache message to service worker with timestamp to prevent deduplication
    navigator.serviceWorker.controller.postMessage({
      type: 'CLEAR_AUTH_CACHE',
      timestamp: Date.now()
    });

    logger.info('Clear auth cache message sent to service worker');
    return true;
  } catch (e) {
    logger.error('Error clearing auth cache:', e);
    return false;
  }
};

/**
 * Clear only Supabase-related cache entries from service worker - optimized
 */
export const clearSupabaseCache = async (): Promise<boolean> => {
  try {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
      logger.info('Service worker not available');
      return false;
    }

    // Send clear Supabase cache message with timestamp to prevent deduplication
    navigator.serviceWorker.controller.postMessage({
      type: 'CLEAR_SUPABASE_CACHE',
      timestamp: Date.now()
    });

    logger.info('Clear Supabase cache message sent to service worker');
    return true;
  } catch (e) {
    logger.error('Error clearing Supabase cache:', e);
    return false;
  }
};

/**
 * Extract tokens from various sources (URL, cookies, localStorage)
 * Useful for fallback auth mechanisms in PWA
 */
export const extractAuthTokens = (): {
  accessToken?: string;
  refreshToken?: string;
  providerToken?: string;
  source?: string;
} => {
  logger.info("Extracting tokens from all possible sources");

  // Initialize result object
  const result: {
    accessToken?: string;
    refreshToken?: string;
    providerToken?: string;
    source?: string;
  } = { source: "none" };

  // 1. First try URL hash fragments (highest priority for PWA)
  try {
    if (window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const providerToken = hashParams.get('provider_token');

      if (accessToken) {
        result.accessToken = accessToken;
        result.source = "url-hash";
        logger.info("Found access token in URL hash");
      }

      if (refreshToken) {
        result.refreshToken = refreshToken;
        result.source = result.source || "url-hash";
        logger.info("Found refresh token in URL hash");
      }

      if (providerToken) {
        result.providerToken = providerToken;
        result.source = result.source || "url-hash";
        logger.info("Found provider token in URL hash");
      }
    }
  } catch (e) {
    logger.error("Error extracting tokens from URL hash:", e);
  }

  // 2. If we don't have tokens from hash, try cookies
  if (!result.accessToken) {
    try {
      const cookies = document.cookie.split(';');

      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (!name || !value) continue;

        if (name === 'sb-access-token-direct' && !result.accessToken) {
          result.accessToken = decodeURIComponent(value);
          result.source = "cookie";
          logger.info("Found access token in cookies");
        }

        if (name === 'sb-refresh-token-direct' && !result.refreshToken) {
          result.refreshToken = decodeURIComponent(value);
          result.source = result.source || "cookie";
          logger.info("Found refresh token in cookies");
        }

        // Also check for the bundle cookie
        if (name === 'pwa_token_bundle' && (!result.accessToken || !result.refreshToken)) {
          try {
            const bundleData = JSON.parse(decodeURIComponent(value));
            if (bundleData.a && !result.accessToken) {
              result.accessToken = bundleData.a;
              logger.info("Found access token in bundle cookie");
            }
            if (bundleData.r && !result.refreshToken) {
              result.refreshToken = bundleData.r;
              logger.info("Found refresh token in bundle cookie");
            }
            result.source = result.source || "cookie-bundle";
          } catch (e) {
            logger.error("Error parsing token bundle:", e);
          }
        }
      }
    } catch (e) {
      logger.error("Error extracting tokens from cookies:", e);
    }
  }

  // 3. If still no tokens, try localStorage backup
  if (!result.accessToken || !result.refreshToken) {
    try {
      const accessToken = localStorage.getItem('sb-access-token-backup');
      const refreshToken = localStorage.getItem('sb-refresh-token-backup');
      const providerToken = localStorage.getItem('provider-token-backup');

      if (accessToken && !result.accessToken) {
        result.accessToken = accessToken;
        result.source = result.source === "none" ? "localStorage" : `${result.source}+localStorage`;
        logger.info("Found access token in localStorage backup");
      }

      if (refreshToken && !result.refreshToken) {
        result.refreshToken = refreshToken;
        result.source = result.source === "none" ? "localStorage" : `${result.source}+localStorage`;
        logger.info("Found refresh token in localStorage backup");
      }

      if (providerToken && !result.providerToken && !result.accessToken) {
        result.providerToken = providerToken;
        result.source = result.source === "none" ? "localStorage" : `${result.source}+localStorage`;
        logger.info("Found provider token in localStorage backup");
      }
    } catch (e) {
      logger.error("Error extracting tokens from localStorage:", e);
    }
  }

  logger.info("Token extraction results:", {
    hasAccessToken: !!result.accessToken,
    hasRefreshToken: !!result.refreshToken,
    hasProviderToken: !!result.providerToken,
    source: result.source
  });

  return result;
};

/**
 * Clear any PWA and server auth related cookies and storage
 * Useful for resetting the state and debugging
 */
export const clearPwaDetectionState = (): void => {
  try {
    logger.info("Clearing all PWA and server auth detection state");

    // Clear localStorage items
    localStorage.removeItem('app_pwa_mode');
    localStorage.removeItem('use_server_auth');
    localStorage.removeItem('app_ios_pwa');
    localStorage.removeItem('token-backup-timestamp');

    // Clear cookies
    document.cookie = "app_pwa_mode=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "mobile_app_login=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "auth_in_progress=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

    logger.info("PWA state cleared successfully");
  } catch (e) {
    logger.error('Error clearing PWA state:', e);
  }
};

// Performance optimization: Proactively register the service worker on module load
if ('serviceWorker' in navigator && isPwaMode()) {
  navigator.serviceWorker.register('/service-worker.js', {
    updateViaCache: 'none',
    scope: '/'
  }).then(registration => {
    logger.info('Proactively registered service worker for PWA');
  }).catch(error => {
    logger.error('Error registering service worker:', error);
  });

  // Also check and persist PWA state
  checkAndPersistPwaState();
}
